import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  compareValues,
  defaultRowKey,
  filterMatches,
  getCollectionFilterLabel,
  hasCollectionUrlState,
  isEmptyFilterValue,
  loadSavedViewsEnvelope,
  mergeFilterDefs,
  normalizeSortStack,
  readField,
  readUrlPreset,
  readUrlState,
  stateToSearch,
  storageKey,
  toText,
} from './collection-utils'
import { serverCollectionQueryKey, useServerCollectionState } from './useCollection-server-state'
import { findActivePreset, findPreset } from './useCollection-presets'
import type { CollectionSavedViewsEnvelope } from './collection-utils'
import type {
  CollectionFilterDef,
  CollectionFilterValue,
  CollectionPageApi,
  CollectionPresetsApi,
  CollectionSavedView,
  CollectionSelection,
  CollectionSort,
  CollectionState,
  CollectionUiState,
  CollectionUrlAdapter,
  CollectionViewsApi,
  UseCollectionConfig,
  UseCollectionResult,
} from './types'

export type {
  CollectionActiveFilter, CollectionColumnDef, CollectionFetcherInput, CollectionFetcherResult,
  CollectionFilterDef, CollectionFilterOption, CollectionFilterState, CollectionFilterValue,
  CollectionNumberRangeValue, CollectionOperator, CollectionOperatorValue, CollectionPresenceOperator,
  CollectionPageApi, CollectionPreset, CollectionPresetsApi, CollectionRangeValue, CollectionSavedView,
  CollectionSelection, CollectionSetOperator, CollectionSort, CollectionSortDirection, CollectionState,
  CollectionStatus, CollectionTextOperator, CollectionUiState, CollectionUrlAdapter, CollectionViewsApi,
  UseCollectionConfig, UseCollectionResult,
} from './types'

export { getCollectionFilterLabel }

const EMPTY_ROWS: never[] = []

interface InitialBundle {
  envelope: CollectionSavedViewsEnvelope
  state: Partial<Pick<CollectionState, 'q' | 'page' | 'pageSize' | 'sort' | 'sortStack' | 'filters'>>
  search: string
}

type UrlAction = 'push' | 'replace'

export function useCollection<RowT>(config: UseCollectionConfig<RowT>): UseCollectionResult<RowT> {
  const isServer = config.mode === 'server'
  const clientInputRows: RowT[] = isServer ? EMPTY_ROWS : config.rows
  const fetcher = isServer ? config.fetcher : null
  const defaultPageSize = config.pageSize ?? 25
  const paginate = config.paginate !== false
  const filterDefs = useMemo(() => mergeFilterDefs(config.columns, config.filters), [config.columns, config.filters])
  const initialRef = useRef<InitialBundle | null>(null)

  if (!initialRef.current) initialRef.current = buildInitialBundle(config, defaultPageSize)

  const [state, setState] = useState<CollectionState>(() => {
    const initial = initialRef.current?.state ?? {}
    const sortStack = normalizeSortStack(initial.sortStack?.length ? initial.sortStack : (initial.sort ? [initial.sort] : []))
    return {
      q: initial.q ?? '',
      page: initial.page ?? 1,
      pageSize: initial.pageSize ?? defaultPageSize,
      sort: sortStack[0] ?? initial.sort ?? null,
      sortStack: sortStack.length ? sortStack : undefined,
      filters: initial.filters ?? {},
      status: isServer ? 'idle' : 'success',
      error: null,
    }
  })
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(() => new Set())
  const [savedEnvelope, setSavedEnvelope] = useState<CollectionSavedViewsEnvelope>(() => initialRef.current?.envelope ?? { version: 1, defaultId: null, views: [] })
  const [refreshNonce, setRefreshNonce] = useState(0)
  const requestId = useRef(0)
  const syncedSearch = useRef(initialRef.current?.search ?? '')
  const fetcherRef = useRef(fetcher)
  const abortRef = useRef<AbortController | null>(null)
  const urlAction = useRef<UrlAction>('replace')

  useEffect(() => {
    fetcherRef.current = fetcher
  }, [fetcher])

  const markUrlAction = useCallback((action: UrlAction) => {
    urlAction.current = action
  }, [])

  const getRowKey = useCallback((row: RowT, index: number) => String(config.getRowKey ? config.getRowKey(row, index) : defaultRowKey(row, index)), [config.getRowKey])
  const sortStack = useMemo(() => normalizeSortStack(state.sortStack?.length ? state.sortStack : (state.sort ? [state.sort] : [])), [state.sort, state.sortStack])
  const sortKey = useMemo(() => sortStack.map((sort) => `${sort.id}:${sort.dir}`).join(','), [sortStack])
  const serverQueryKey = useMemo(
    () => serverCollectionQueryKey({ q: state.q, pageSize: state.pageSize, sort: state.sort, sortStack, filters: state.filters }),
    [sortStack, state.filters, state.pageSize, state.q, state.sort],
  )
  const serverState = useServerCollectionState<RowT>(isServer, serverQueryKey)
  const serverRows = serverState.rows
  const serverTotal = serverState.total
  const hasServerTotal = serverState.hasTotal
  const sourceRows = isServer ? serverRows : clientInputRows

  const clientRows = useMemo(() => {
    if (isServer) return serverRows
    const q = state.q.trim().toLowerCase()
    const searched = q
      ? clientInputRows.filter((row) => config.columns.some((column) => toText(column.searchValue ? column.searchValue(row) : readField(row, column.id)).toLowerCase().includes(q)))
      : clientInputRows
    const filtered = searched.filter((row) => filterDefs.every((filter) => filterMatches(row, filter, state.filters[filter.id])))
    if (!sortStack.length) return filtered
    return filtered
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        for (const sort of sortStack) {
          const column = config.columns.find((item) => item.id === sort.id)
          const av = column?.sortAccessor ? column.sortAccessor(a.row) : readField(a.row, sort.id)
          const bv = column?.sortAccessor ? column.sortAccessor(b.row) : readField(b.row, sort.id)
          const compared = compareValues(av, bv)
          if (compared !== 0) return sort.dir === 'desc' ? -compared : compared
        }
        return a.index - b.index
      })
      .map((entry) => entry.row)
  }, [clientInputRows, config.columns, filterDefs, isServer, serverRows, sortStack, state.filters, state.q])

  const total = isServer ? serverTotal : clientRows.length
  const effectivePageSize = paginate ? state.pageSize : Math.max(total, sourceRows.length, state.pageSize, 1)
  const totalPages = paginate ? Math.max(1, Math.ceil(total / effectivePageSize)) : 1
  const safePage = paginate
    ? isServer && !hasServerTotal
      ? Math.max(state.page, 1)
      : Math.min(Math.max(state.page, 1), totalPages)
    : 1
  const rows = isServer || !paginate ? (isServer ? serverRows : clientRows) : clientRows.slice((safePage - 1) * effectivePageSize, safePage * effectivePageSize)
  const from = total === 0 ? 0 : (safePage - 1) * effectivePageSize + 1
  const to = total === 0 ? 0 : Math.min(safePage * effectivePageSize, total)
  const isRefreshing = isServer && state.status === 'loading' && Boolean(config.keepRowsWhileRefreshing) && serverRows.length > 0
  const uiState: CollectionUiState = state.status === 'loading' && !isRefreshing ? 'loading' : state.status === 'error' ? 'error' : total === 0 ? 'empty' : 'ready'
  const activePresetId = useMemo(() => findActivePreset(config.presets ?? [], state), [config.presets, state.filters, state.q, state.sort])

  useEffect(() => {
    if (safePage !== state.page) {
      markUrlAction('replace')
      setState((current) => ({ ...current, page: safePage }))
    }
  }, [markUrlAction, safePage, state.page])

  useEffect(() => {
    if (!isServer || !fetcherRef.current) return
    const id = requestId.current + 1
    requestId.current = id
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setState((current) => ({ ...current, status: 'loading', error: null }))
    fetcherRef.current({
      page: paginate ? safePage : 1,
      pageSize: paginate ? state.pageSize : Number.MAX_SAFE_INTEGER,
      sort: state.sort,
      sortStack: config.multiSort ? sortStack : undefined,
      filters: state.filters,
      q: state.q,
      signal: controller.signal,
    })
      .then((result) => {
        if (requestId.current !== id || controller.signal.aborted) return
        serverState.accept(result)
        setState((current) => ({ ...current, status: 'success', error: null }))
      })
      .catch((error: unknown) => {
        if (requestId.current !== id || controller.signal.aborted) return
        serverState.clear()
        setState((current) => ({ ...current, status: 'error', error }))
      })
    return () => controller.abort()
  }, [config.multiSort, isServer, paginate, refreshNonce, safePage, serverQueryKey, serverState.accept, serverState.clear, sortKey, state.filters, state.pageSize, state.q, state.sort])

  useEffect(() => {
    if (!isServer && state.status !== 'success') setState((current) => ({ ...current, status: 'success', error: null }))
  }, [isServer, state.status])

  useEffect(() => {
    if (config.urlSync !== 'query') return
    const currentSearch = readCurrentSearch(config.urlAdapter)
    const nextSearch = stateToSearch({ ...state, page: safePage }, {
      search: currentSearch,
      defaultPageSize,
      urlKey: config.urlKey,
      urlFilterFormat: config.urlFilterFormat,
      presetId: activePresetId,
    })
    if (nextSearch === syncedSearch.current) return
    syncedSearch.current = nextSearch
    const replace = urlAction.current === 'replace'
    urlAction.current = 'replace'
    writeSearch(config.urlAdapter, nextSearch, replace)
  }, [activePresetId, config.urlAdapter, config.urlFilterFormat, config.urlKey, config.urlSync, defaultPageSize, safePage, state.filters, state.page, state.pageSize, state.q, state.sort, state.sortStack])

  useEffect(() => {
    if (config.urlSync !== 'query') return
    const onUrlChange = () => {
      const search = readCurrentSearch(config.urlAdapter)
      syncedSearch.current = search
      const next = readUrlState({ search, urlKey: config.urlKey, urlFilterFormat: config.urlFilterFormat })
      const preset = findPreset(config.presets, readUrlPreset(search, config.urlKey))
      setState((current) => {
        const merged = normalizeStatePatch({ ...current, q: '', page: 1, pageSize: defaultPageSize, sort: null, sortStack: undefined, filters: {}, ...preset?.state, ...next })
        return { ...current, ...merged }
      })
    }
    if (config.urlAdapter?.subscribe) return config.urlAdapter.subscribe(onUrlChange)
    if (typeof window === 'undefined') return undefined
    window.addEventListener('popstate', onUrlChange)
    return () => window.removeEventListener('popstate', onUrlChange)
  }, [config.presets, config.urlAdapter, config.urlFilterFormat, config.urlKey, config.urlSync, defaultPageSize])

  useEffect(() => {
    if (!config.views || typeof window === 'undefined') return
    window.localStorage.setItem(storageKey(config.views), JSON.stringify(savedEnvelope))
  }, [config.views, savedEnvelope])

  const activeFilters = useMemo(() => filterDefs
    .map((filter) => ({ filter, value: state.filters[filter.id] }))
    .filter((entry): entry is { filter: CollectionFilterDef<RowT>; value: CollectionFilterValue } => !isEmptyFilterValue(entry.value))
    .map(({ filter, value }) => ({ id: filter.id, name: filter.label, label: getCollectionFilterLabel(filter, value), value })), [filterDefs, state.filters])

  const setPage = useCallback((page: number) => {
    markUrlAction('replace')
    setState((current) => ({ ...current, page: Math.max(1, page) }))
  }, [markUrlAction])

  const setPageSize = useCallback((pageSize: number) => {
    markUrlAction('replace')
    setState((current) => ({ ...current, page: 1, pageSize: Math.max(1, pageSize) }))
  }, [markUrlAction])

  const setQuery = useCallback((query: string) => {
    markUrlAction('push')
    setState((current) => ({ ...current, page: 1, q: query }))
  }, [markUrlAction])

  const setSort = useCallback((sort: CollectionState['sort']) => {
    markUrlAction('push')
    setState((current) => ({ ...current, page: 1, sort, sortStack: sort ? [sort] : undefined }))
  }, [markUrlAction])

  const setSortStack = useCallback((stack: CollectionSort[]) => {
    markUrlAction('push')
    setState((current) => {
      const nextStack = normalizeSortStack(stack)
      return { ...current, page: 1, sort: nextStack[0] ?? null, sortStack: nextStack.length ? nextStack : undefined }
    })
  }, [markUrlAction])

  const toggleSort = useCallback((id: string, opts?: { append?: boolean }) => {
    markUrlAction('push')
    setState((current) => {
      const append = Boolean(opts?.append && config.multiSort)
      if (!append) {
        const sort = current.sort?.id === id ? (current.sort.dir === 'asc' ? { id, dir: 'desc' as const } : null) : { id, dir: 'asc' as const }
        return { ...current, page: 1, sort, sortStack: sort ? [sort] : undefined }
      }
      const stack = normalizeSortStack(current.sortStack?.length ? current.sortStack : (current.sort ? [current.sort] : []))
      const index = stack.findIndex((sort) => sort.id === id)
      const next = [...stack]
      if (index === -1) {
        next.push({ id, dir: 'asc' })
      } else if (next[index].dir === 'asc') {
        next[index] = { id, dir: 'desc' }
      } else {
        next.splice(index, 1)
      }
      const nextStack = normalizeSortStack(next)
      return { ...current, page: 1, sort: nextStack[0] ?? null, sortStack: nextStack.length ? nextStack : undefined }
    })
  }, [config.multiSort, markUrlAction])

  const setFilter = useCallback((id: string, value: CollectionFilterValue | undefined) => {
    markUrlAction('push')
    setState((current) => ({ ...current, page: 1, filters: { ...current.filters, [id]: value } }))
  }, [markUrlAction])

  const clearFilter = useCallback((id: string) => {
    markUrlAction('push')
    setState((current) => {
      const filters = { ...current.filters }
      delete filters[id]
      return { ...current, page: 1, filters }
    })
  }, [markUrlAction])

  const clearFilters = useCallback(() => {
    markUrlAction('push')
    setState((current) => ({ ...current, page: 1, filters: {} }))
  }, [markUrlAction])

  const applyPreset = useCallback((id: string) => {
    const preset = findPreset(config.presets, id)
    if (!preset) return
    markUrlAction('push')
    setState((current) => ({ ...current, ...normalizeStatePatch({ ...preset.state, page: 1 }) }))
  }, [config.presets, markUrlAction])

  const clearPreset = useCallback(() => {
    markUrlAction('push')
    setState((current) => ({ ...current, q: '', page: 1, sort: null, sortStack: undefined, filters: {} }))
  }, [markUrlAction])

  const refresh = useCallback(() => {
    if (isServer) setRefreshNonce((current) => current + 1)
  }, [isServer])

  const selectedRows = useMemo(() => sourceRows.filter((row, index) => selectedKeys.has(getRowKey(row, index))), [getRowKey, selectedKeys, sourceRows])
  const visibleKeys = useMemo(() => rows.map((row, index) => getRowKey(row, index)), [getRowKey, rows])
  const allVisibleSelected = visibleKeys.length > 0 && visibleKeys.every((key) => selectedKeys.has(key))
  const someVisibleSelected = visibleKeys.some((key) => selectedKeys.has(key))
  const selection = buildSelection(selectedKeys, selectedRows, getRowKey, visibleKeys, rows, setSelectedKeys, allVisibleSelected, someVisibleSelected)
  const views = buildViews(Boolean(config.views), savedEnvelope, setSavedEnvelope, state, (next) => {
    markUrlAction('push')
    setState((current) => ({ ...current, ...normalizeStatePatch({ ...next, page: 1 }) }))
  })
  const presets: CollectionPresetsApi = { list: config.presets ?? [], activeId: activePresetId, apply: applyPreset, clear: clearPreset }
  const page: CollectionPageApi = { page: safePage, pageSize: state.pageSize, total, totalPages, from, to, setPage, setPageSize }

  return {
    rows,
    allRows: isServer ? serverRows : clientRows,
    columns: config.columns,
    filterDefs,
    activeFilters,
    state: { ...state, page: safePage, sortStack: sortStack.length ? sortStack : undefined },
    uiState,
    total,
    totalPages,
    from,
    to,
    page,
    selection,
    views,
    presets,
    isRefreshing,
    refresh,
    getRowKey,
    setSort,
    setSortStack,
    toggleSort,
    setFilter,
    clearFilter,
    clearFilters,
    setQuery,
    setPage,
    setPageSize,
  }
}

function buildInitialBundle<RowT>(config: UseCollectionConfig<RowT>, defaultPageSize: number): InitialBundle {
  const search = config.urlSync === 'query' ? readCurrentSearch(config.urlAdapter) : ''
  const envelope = loadSavedViewsEnvelope(config.views)
  const urlOptions = { search, urlKey: config.urlKey, urlFilterFormat: config.urlFilterFormat }
  const hasUrlState = config.urlSync === 'query' && hasCollectionUrlState(search, urlOptions)
  const preset = config.urlSync === 'query' ? findPreset(config.presets, readUrlPreset(search, config.urlKey)) : undefined
  const defaultView = !hasUrlState && envelope.defaultId ? envelope.views.find((view) => view.id === envelope.defaultId) : undefined
  const state = normalizeStatePatch({
    pageSize: defaultPageSize,
    ...config.initialState,
    ...defaultView?.state,
    ...preset?.state,
    ...(config.urlSync === 'query' ? readUrlState(urlOptions) : {}),
  })
  return { envelope, state, search }
}

function readCurrentSearch(adapter?: CollectionUrlAdapter): string {
  if (adapter) return adapter.read()
  return typeof window === 'undefined' ? '' : window.location.search
}

function writeSearch(adapter: CollectionUrlAdapter | undefined, search: string, replace: boolean) {
  if (adapter) {
    adapter.write(search, { replace })
    return
  }
  if (typeof window === 'undefined') return
  const nextUrl = `${window.location.pathname}${search}${window.location.hash}`
  if (replace) {
    window.history.replaceState(null, '', nextUrl)
  } else {
    window.history.pushState(null, '', nextUrl)
  }
}

function normalizeStatePatch<T extends Partial<Pick<CollectionState, 'sort' | 'sortStack'>>>(patch: T): T {
  const stack = normalizeSortStack(patch.sortStack?.length ? patch.sortStack : (patch.sort ? [patch.sort] : []))
  return {
    ...patch,
    sort: stack[0] ?? patch.sort ?? null,
    sortStack: stack.length ? stack : undefined,
  }
}

function buildSelection<RowT>(
  selectedKeys: Set<string>,
  selectedRows: RowT[],
  getRowKey: (row: RowT, index: number) => string,
  visibleKeys: string[],
  rows: RowT[],
  setSelectedKeys: (updater: (current: Set<string>) => Set<string>) => void,
  allVisibleSelected: boolean,
  someVisibleSelected: boolean,
): CollectionSelection<RowT> {
  return {
    selectedKeys,
    selectedRows,
    count: selectedKeys.size,
    isSelected: (row, index) => selectedKeys.has(getRowKey(row, index)),
    setRowSelected: (row, index, selected) => setSelectedKeys((current) => updateOne(current, getRowKey(row, index), selected)),
    toggleRow: (row, index) => setSelectedKeys((current) => updateOne(current, getRowKey(row, index), !current.has(getRowKey(row, index)))),
    clear: () => setSelectedKeys(() => new Set()),
    selectAllVisible: () => setSelectedKeys((current) => new Set([...current, ...visibleKeys])),
    toggleAllVisible: () => setSelectedKeys((current) => {
      const next = new Set(current)
      const shouldClear = rows.length > 0 && visibleKeys.every((key) => next.has(key))
      visibleKeys.forEach((key) => shouldClear ? next.delete(key) : next.add(key))
      return next
    }),
    allVisibleSelected,
    someVisibleSelected,
  }
}

function updateOne(current: Set<string>, key: string, selected: boolean): Set<string> {
  const next = new Set(current)
  if (selected) {
    next.add(key)
  } else {
    next.delete(key)
  }
  return next
}

function buildViews(
  enabled: boolean,
  envelope: CollectionSavedViewsEnvelope,
  setEnvelope: (updater: (current: CollectionSavedViewsEnvelope) => CollectionSavedViewsEnvelope) => void,
  state: CollectionState,
  applyState: (next: CollectionSavedView['state']) => void,
): CollectionViewsApi {
  return {
    enabled,
    saved: envelope.views,
    defaultId: envelope.defaultId,
    save: (name) => setEnvelope((current) => {
      const cleanName = name.trim()
      if (!cleanName) return current
      const id = cleanName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `view-${Date.now()}`
      const view: CollectionSavedView = { id, name: cleanName, state: { q: state.q, pageSize: state.pageSize, sort: state.sort, sortStack: state.sortStack, filters: state.filters } }
      return { version: 1, defaultId: current.defaultId, views: [...current.views.filter((item) => item.id !== id), view] }
    }),
    apply: (id) => {
      const view = envelope.views.find((item) => item.id === id)
      if (view) applyState(view.state)
    },
    remove: (id) => setEnvelope((current) => ({ version: 1, defaultId: current.defaultId === id ? null : current.defaultId, views: current.views.filter((view) => view.id !== id) })),
    rename: (id, name) => setEnvelope((current) => ({
      version: 1,
      defaultId: current.defaultId,
      views: current.views.map((view) => view.id === id ? { ...view, name: name.trim() || view.name } : view),
    })),
    setDefault: (id) => setEnvelope((current) => ({ version: 1, defaultId: id && current.views.some((view) => view.id === id) ? id : null, views: current.views })),
  }
}
